import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { useData } from "../lib/DataContext";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function QuickCreateTask({ onClose, onCreated }) {
  const { users, departments, projects } = useData();
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "", primary_owner_id: "", project_id: "", department_id: "",
    priority: "Medium", current_due_date: "", description: "",
    current_action_owner_id: "", accountable_owner_id: "", workflow_type: "simple",
    execution_status: "Open",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Task title is required.");
    if (!f.primary_owner_id) return toast.error("Primary Owner is required.");
    setSaving(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
      payload.title = f.title;
      const { data } = await api.post("/tasks", payload);
      toast.success(`Task ${data.human_id} created`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="quick-create-drawer">
        <SheetHeader>
          <SheetTitle>Create Task</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-5">
          <div>
            <Label>Task Title *</Label>
            <Input data-testid="qc-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="What needs to be done?" />
          </div>
          <div>
            <Label>Primary Owner *</Label>
            <Select value={f.primary_owner_id} onValueChange={(v) => set("primary_owner_id", v)}>
              <SelectTrigger data-testid="qc-owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Project</Label>
              <Select value={f.project_id} onValueChange={(v) => set("project_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={f.department_id} onValueChange={(v) => set("department_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="qc-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Critical", "High", "Medium", "Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" data-testid="qc-due" value={f.current_due_date || ""} onChange={(e) => set("current_due_date", e.target.value)} />
            </div>
          </div>

          <button className="flex items-center gap-1 text-sm text-blue-600 font-medium" onClick={() => setMore(!more)}>
            {more ? <ChevronUp size={15} /> : <ChevronDown size={15} />} More Details
          </button>

          {more && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label>Description</Label>
                <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Accountable Owner</Label>
                  <Select value={f.accountable_owner_id} onValueChange={(v) => set("accountable_owner_id", v)}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Current Action Owner</Label>
                  <Select value={f.current_action_owner_id} onValueChange={(v) => set("current_action_owner_id", v)}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Closure Workflow</Label>
                <Select value={f.workflow_type} onValueChange={(v) => set("workflow_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">Simple (Complete → Closed)</SelectItem>
                    <SelectItem value="controlled">Controlled (Review required)</SelectItem>
                    <SelectItem value="approval">Approval (Approval required)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button data-testid="qc-submit" onClick={submit} disabled={saving} className="flex-1">
              {saving ? "Creating…" : "Create Task"}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
